import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const Code = makeShortcode("Code");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>{`Overview`}</h2>
    <p>{`Links are user interface elements that `}<em parentName="p">{`navigate`}</em>{` you to a new place or new content. Contrast this with buttons, which are designed to `}<em parentName="p">{`activate`}</em>{` a feature.`}</p>
    <h2>{`Why?`}</h2>
    <p>{`Links can do things like help with page context, reference similar items of interest, and allow for endless connected information surfing through sites like Wikipedia. Links play a key part in your experience on the web, but without proper consideration they can be frustrating to use, skipped over, or completely unnoticed. `}</p>
    <p>{`For screen reader assistive technology, links and buttons are expected to function differently from each other. If a link is activated and does not do what was expected, that can be disorienting and frustrating. `}</p>
    <p>{`A common way a screen reader might navigate the page is by going through a list of all the links on the page. Without context, "read more" or "click here" links are not helpful. `}</p>
    <p>{`People who have low or colorblind vision may have trouble identifying links that just use color to distinguish them from plain text, this is why keeping the underline styling on links within body text is important for identification.`}</p>
    <h2>{`How to test links`}</h2>
    <ul>
      <li parentName="ul">{`Check a link's function. If clicking a link activates a feature on the page, it should be a `}<inlineCode parentName="li">{`<button>`}</inlineCode>{`, and vice versa. `}</li>
      <li parentName="ul">{`Check a link's purpose. If you can't get an idea of where a link will take you based on the link text alone, the link text should be updated. `}<a parentName="li" {...{
          "href": "#writing-link-text"
        }}>{`Learn how to write good link text`}</a>{`.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://webaim.org/resources/contrastchecker/"
        }}>{`Check a link's contrast`}</a>{`. Because links are text-based, they need to pass 4.5:1 against the background color that they are on.`}</li>
      <li parentName="ul">{`In body text, like a Markdown file, check to make sure that all links have underline styling. `}</li>
    </ul>
    <h2>{`Guidelines`}</h2>
    <h3>{`For designers`}</h3>
    <ul>
      <li parentName="ul">{`Make sure you use the correct accent color for links in your designs. Double check the color you're using if you're using it on anything other than a white background. For more context on color, check out our `}<a parentName="li" {...{
          "href": "https://primer.style/design/foundations/color"
        }}>{`color documentation`}</a>{`.`}</li>
      <li parentName="ul">{`Make sure a link's purpose can be understood on its own without the surrounding context in concise words. `}</li>
      <li parentName="ul">{`Links should always be underlined if they're in body text. `}</li>
      <li parentName="ul">{`Links should look like links, not buttons, except in rare circumstances, like calls to action. If the action takes you somewhere rather than doing something on the page, it should be semantically coded as a link regardless of the visual style.`}</li>
    </ul>
    <DoDontContainer mdxType="DoDontContainer">
    <Do mdxType="Do">
        <img src="https://user-images.githubusercontent.com/40274682/160483376-a88eb64f-ce80-4a11-93b5-6d882b1970eb.png" alt="Markdown paragraph with descriptive links" />
        <Caption mdxType="Caption">Be descriptive with your links so that they can stand alone and be understood.</Caption>
    </Do>
    <Dont mdxType="Dont">
        <img src="https://user-images.githubusercontent.com/40274682/160474793-2fd3085f-0813-43ed-8b43-c1a5f14aaea9.png" alt="Markdown paragraph with links like 'Click here' and 'find out more'" />
        <Caption mdxType="Caption">Don't use generic terms like 'click here' that can't be understood out of context.</Caption>
    </Dont>
    </DoDontContainer>
    <DoDontContainer mdxType="DoDontContainer">
    <Do mdxType="Do">
        <img src="https://user-images.githubusercontent.com/40274682/160474794-06a75b30-06d3-475e-863f-605e8a6dad72.png" alt="Underlined username link in issue subheading" />
        <img src="https://user-images.githubusercontent.com/40274682/160474798-175a588f-4a03-4b42-8787-0af13bc71311.png" alt="Underlined community guidelines link in text under a markdown comment text area." />
        <Caption mdxType="Caption">Underline links in paragraphs and sentences</Caption>
    </Do>
    <Dont mdxType="Dont">
        <img src="https://user-images.githubusercontent.com/40274682/160474800-b505369e-1730-4c14-b426-70b5bd016a6c.png" alt="Issue subheader with none of the links underlined" />
        <img src="https://user-images.githubusercontent.com/40274682/160474802-3fe6b792-3c79-40ce-a443-c2279a3a785b.png" alt="GitHub commnuity guideline link not underlined in sentence." />
        <Caption mdxType="Caption">Don't forget to underline links in paragraphs and sentences</Caption>
    </Dont>
    </DoDontContainer>
    <h3>{`For engineers`}</h3>
    <ul>
      <li parentName="ul">{`Use Primer link components:`}
        <ul parentName="li">
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://primer.style/view-components/components/link"
            }}>{`Link in Primer ViewComponents`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://primer.style/react/Link"
            }}>{`Link in Primer React`}</a></li>
        </ul>
      </li>
      <li parentName="ul">{`Don't override the link styling provided in the components`}</li>
      <li parentName="ul">{`Make sure links receive our global focus indicator styling when navigating the page with a keyboard (reference: `}<a parentName="li" {...{
          "href": "https://primer.style/design/accessibility/focus-management"
        }}>{`Focus management`}</a>{`)`}</li>
      <li parentName="ul">{`Don't use the `}<inlineCode parentName="li">{`title`}</inlineCode>{` attribute`}
        <ul parentName="li">
          <li parentName="ul">{`Content within a `}<inlineCode parentName="li">{`title`}</inlineCode>{` is inaccessible for many users, such as touch-only and keyboard-only users. If additional content needs to be associated with a link consider using a `}<a parentName="li" {...{
              "href": "https://primer.style/view-components/components/alpha/tooltip"
            }}>{`tooltip`}</a>{` or `}<a parentName="li" {...{
              "href": "https://primer.style/design/accessibility/tooltip-alternatives"
            }}>{`alternatives to a tooltip`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Avoid adding side effects to link click events. Links should navigate, not affect the page.`}</li>
    </ul>
    <h2>{`Common mistakes`}</h2>
    <ul>
      <li parentName="ul">{`Only adding an underline to a link when it has focus or is hovered over`}</li>
      <li parentName="ul">{`Underlining every link on the page. For example, a navigation list is a list of links but navigational links don't have to be underlined because the intent is understood and an underline is not needed to identify the interactive nature of the control.`}</li>
    </ul>
    <h2>{`Writing link text`}</h2>
    <p>{`Link text should be descriptive enough to convey the destination without relying on the surrounding text. Screen reader users often tab through links on a page to quickly find content without needing to listen to the full page.
When link text is too generic like "Read more", the destination of the link is not conveyed. `}</p>
    <p>{`It may be acceptable in some scenarios to provide a more descriptive link text for screen reader users by setting the `}<inlineCode parentName="p">{`aria-label`}</inlineCode>{`. However, this technique will result in divergence between the label and the text and is not an ideal, future-proof solution.
Whenever possible, prefer a single descriptive link text that is available for both sighted users and screen reader users.`}</p>
    <p>{`If you must resort to the ARIA technique to provide a descriptive link text, follow these principles:`}</p>
    <ol>
      <li parentName="ol">{`The visible text is included in full as part of the accessible name to avoid a violation of `}<a parentName="li" {...{
          "href": "https://www.w3.org/WAI/WCAG21/Understanding/label-in-name.html"
        }}>{`SC 2.5.3: Label in Name`}</a>{`.`}</li>
      <li parentName="ol">{`The sentence that the link is a part of is well-formed and grammatical when read with both the visible text and the accessible name.`}</li>
    </ol>
    <DoDontContainer mdxType="DoDontContainer">
    <Do mdxType="Do">
      <Caption mdxType="Caption">Accessible name fully includes the visible link text, "Learn more" and is well-formed with either label.</Caption>
      <Code className="language-html" mdxType="Code">{`There are various plans available. <a href="..." aria-label="Learn more about GitHub pricing plans">Learn more</button>`}</Code>
      <br />
    </Do>
    <Dont mdxType="Dont">
      <Caption mdxType="Caption">Accessible name results in poorly-formed sentence, "Learn more about keyboard shortcuts allow you to access common commands more quickly". </Caption>
      <Code className="language-html" mdxType="Code">{`<a href="..." aria-label="Learn more about keyboard shortcuts">Keyboard shortcuts</a> allow you to access common commands more quickly.`}</Code>
    </Dont>
    </DoDontContainer>
    <p>{`As demonstrated in the examples, this technique adds more complexity to the code and can introduce more problems than it solves so only use this technique if absolutely necessary.`}</p>
    <h2>{`Additional resources`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://dequeuniversity.com/class/visual-design/color/distinguish-links-from-text"
        }}>{`Color to Distinguish Links from Text`}</a>{` (requires a subscription to access the content)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://accessibilityinsights.io/en/"
        }}>{`Accessibility Insights plugin`}</a>{`: With the Accessibility Insights plugin installed, open the plugin, visit the `}<strong parentName="li">{`Assessment`}</strong>{` option and select `}<strong parentName="li">{`7. Links`}</strong></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://webaim.org/techniques/hypertext/"
        }}>{`WebAIM: Links and Hypertext - Introduction to Links and Hypertext`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/github/erblint-github/blob/main/docs/rules/accessibility/avoid-generic-link-text-counter.md"
        }}>{`erblint-github: Avoid generic link text`}</a></li>
    </ul>
    <h3>{`Related WCAG guidelines and success criteria`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/WCAG21/Understanding/name-role-value.html"
        }}>{`Understanding Success Criterion 4.1.2: Name, Role, Value`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-practices-1.2/#link"
        }}>{`Authoring Practices Guide - Links`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-1.2/#link"
        }}>{`Accessible Rich Internet Applications (WAI-ARIA) 1.2 (w3.org)`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/WCAG21/Understanding/link-purpose-in-context.html"
        }}>{`Understanding Success Criterion 2.4.4: Link Purpose (In Context)`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      